import { creatorbase, creatorbaseQueries } from '@round/api';
import { GenericDropdownOption } from 'App.types';
import useNonNullContext from 'Hooks/useNonNullContext';
import useUpdateProjectCampaign from '../../hooks/useUpdateProjectCampaign';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import { useMemo } from 'react';
import TiktokCampaignForm, {
    mapTiktokCampaignApiErrorsToFormikErrors,
    TiktokCampaignFormValues,
} from '../components/TiktokCampaignForm';
import useTikTokAudio from 'Modules/TikTok/hooks/useTikTokAudio';
import useAbortableEffect from 'Hooks/useAbortableEffect';
import { FormikProvider, useFormik, yupToFormErrors } from 'formik';
import { Modal } from '@round/whitelabel-ui';
import { Button } from '@round/whitelabel-ui';
import { omit } from 'lodash';
import {
    mapCampaignFormValuesToApiData,
    sharedCampaignValidationSchema,
    validationSchema as validationSchemaBase,
} from '../../components/CampaignFormBase/CampaignFormBase';
import { getObjectDifference, showNotification } from 'helpers';
import { useAllPosts, useClient, useUsers } from '@round/creatorbase-helpers/api-hooks';
import { useCurrentCampaignPaymentDetails } from '../../components/ProjectCampaignsTable/CampaignPaymentRequestModal/useCurrentCampaignPaymentDetails';
import { mapUserToOption } from 'Modules/Plans/User/hooks/useUsersSelect';
import styles from './UpdateTiktokCampaignModal.module.css';
import useProjectDetails from 'Modules/Plans/Project/ProjectDetails/useProjectDetails';
import { getProjectDetailsPostsParams } from 'Modules/Plans/Project/ProjectDetails/hooks/posts/getProjectDetailsPostsParams';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import { backupPostsStatuses } from 'Modules/Plans/Posts/helpers';
import { isCampaignCostLockedByPayment } from 'Modules/Plans/Campaigns/helpers';
import * as yup from 'yup';

type Props = {
    isOpen: boolean;
    closeModal: () => void;
    campaign: creatorbase.TiktokCampaign | null;
};

const UpdateTiktokCampaignModal = ({ isOpen, closeModal, campaign }: Props) => {
    const { update: updateCampaign } = useUpdateProjectCampaign();
    const isNotManagedByCreatorbase = Boolean(campaign?.managed_by && campaign?.managed_by !== 'creatorbase');

    const { currencies } = useNonNullContext(OptionsContext);
    const currency = currencies
        .map<GenericDropdownOption<number>>((c) => ({ value: c.id, label: c.name }))
        .find((c) => c.value === campaign?.currency_id);

    const { team, project } = useProjectDetails();
    const { client } = useClient(team?.client_id);
    const isCampaignPaymentRequestsEnabled = useFeatureFlags().isEnabled('rou_539_campaign_payment_requests');
    const arePaymentsEnabled = isCampaignPaymentRequestsEnabled && !!client?.is_payment_enabled;
    const shouldFetchPaymentDetails = arePaymentsEnabled && isOpen && campaign?.is_post_cost_editable === false;
    const { campaignPaymentDetails } = useCurrentCampaignPaymentDetails(
        { campaignId: campaign?.id, paymentRequestId: campaign?.payment_request_id },
        { enabled: shouldFetchPaymentDetails }
    );
    const {
        data: campaignPosts,
        isLoading: arePostsLoading,
        hasError: postsHaveError,
    } = useAllPosts(getProjectDetailsPostsParams(campaign?.id ?? 0), { enabled: !!campaign && isOpen });

    const {
        fetchData: fetchAudio,
        data: tiktokAudio,
        status: tiktokAudioStatus,
        reset: resetTiktokAudio,
    } = useTikTokAudio();

    useAbortableEffect(
        (signal) => {
            const isInitialized = tiktokAudioStatus === 'success' || tiktokAudioStatus === 'error';
            if (campaign?.tiktok_details?.audio_id && !isInitialized) {
                fetchAudio(campaign.tiktok_details.audio_id, { signal }).catch(() => {});
            }
        },
        [campaign?.tiktok_details?.audio_id, fetchAudio, tiktokAudioStatus]
    );

    const teamMembers = campaign?.team_members;
    const { data: users } = useUsers(
        { id: teamMembers?.join(), page_size: teamMembers?.length },
        { enabled: !!teamMembers?.length && isOpen }
    );

    const initialValues = useMemo<TiktokCampaignFormValues>(
        () => ({
            cost: String((shouldFetchPaymentDetails ? campaignPaymentDetails?.base_cost : campaign?.cost) ?? ''),
            currency: currency,
            is_post_cost_editable: campaign?.is_post_cost_editable ?? null,
            name: campaign?.name ?? '',
            audio_identifier: tiktokAudio?.tiktok_id ?? '',
            team_members: users?.results.map(mapUserToOption),
        }),
        [
            shouldFetchPaymentDetails,
            campaignPaymentDetails?.base_cost,
            campaign?.cost,
            campaign?.is_post_cost_editable,
            campaign?.name,
            currency,
            tiktokAudio,
            users?.results,
        ]
    );

    const formik = useFormik<TiktokCampaignFormValues>({
        initialValues,
        onSubmit: (values, helpers) => {
            if (values.is_post_cost_editable === null || !campaign?.id) {
                helpers.setSubmitting(false);
                return;
            }

            const mappedFormValues = mapCampaignFormValuesToApiData(values, arePaymentsEnabled);
            const updatedFields = getObjectDifference(mappedFormValues, campaign);
            const hasAudioIdentifierChanged = values.audio_identifier !== initialValues.audio_identifier;

            updateCampaign(
                {
                    id: campaign.id,
                    ...updatedFields,
                    ...(hasAudioIdentifierChanged && {
                        tiktok_details: { audio_identifier: values.audio_identifier },
                    }),
                },
                {
                    onSuccess: () => {
                        showNotification('Campaign updated', 'info');
                        helpers.resetForm();
                        resetTiktokAudio();
                        closeModal();
                    },
                    onError: (error) => {
                        if (
                            creatorbaseQueries.campaigns.utils.update.isExpectedErrorResponse(error) &&
                            error.response.status === 400
                        ) {
                            helpers.setErrors(mapTiktokCampaignApiErrorsToFormikErrors(error.response.data));
                            return;
                        }

                        showNotification('Could not update campaign', 'error');
                    },
                    onSettled: () => {
                        helpers.setSubmitting(false);
                    },
                }
            );
        },
        // validationSchema param threw an error on validation
        validate: async (values) => {
            const validationSchema =
                tiktokAudioStatus === 'error'
                    ? validationSchemaBase
                    : validationSchemaBase.shape({
                          audio_identifier: yup.string().optional(),
                      });

            try {
                if (isNotManagedByCreatorbase) {
                    await sharedCampaignValidationSchema.validate(values, { abortEarly: false });
                    return;
                }

                const transformedValues = values.is_post_cost_editable === false ? values : omit(values, 'cost');
                await validationSchema.validate(transformedValues, { abortEarly: false });
            } catch (e) {
                return yupToFormErrors(e);
            }
        },
        enableReinitialize: true,
    });

    const handleClose = () => {
        formik.resetForm();
        resetTiktokAudio();
        closeModal();
    };

    // When the posts fetch fails we can't confirm the campaign is free of
    // costed posts, so keep the spending type locked rather than letting it
    // silently fall open.
    const isSpendingTypeDisabled =
        campaign?.is_post_cost_editable &&
        (postsHaveError ||
            campaignPosts.some(
                (post) =>
                    post.cost !== null && !backupPostsStatuses.includes(post.status as creatorbase.BackupPostStatus)
            ));
    const isPaymentLocked = isCampaignCostLockedByPayment(campaign?.payment_status);

    const readonlyFieldConditions: { [k in keyof TiktokCampaignFormValues]?: boolean } = {
        is_post_cost_editable: isSpendingTypeDisabled || isNotManagedByCreatorbase || isPaymentLocked,
        cost: isNotManagedByCreatorbase || isPaymentLocked,
        currency:
            (project?.campaign_count && project?.campaign_count > 1) || isNotManagedByCreatorbase || isPaymentLocked,
        audio_identifier: isNotManagedByCreatorbase,
    };

    const readonlyFields = Object.entries(readonlyFieldConditions)
        .filter(([, condition]) => !!condition)
        .map(([field]) => field as keyof TiktokCampaignFormValues);

    return (
        <Modal className={styles.modal} closeOnOverlayClick isOpen={isOpen} onClose={handleClose}>
            <Modal.Header>
                <Modal.Title>Edit campaign</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <FormikProvider value={formik}>
                    <TiktokCampaignForm
                        readonlyFields={readonlyFields}
                        teamId={team?.id}
                        campaignId={campaign?.id}
                        paymentRequestId={campaign?.payment_request_id}
                        isManagedExternally={isNotManagedByCreatorbase}
                        audioTitle={tiktokAudio?.title ?? ''}
                        audioAuthor={tiktokAudio?.author_name ?? ''}
                        audioIdentifier={tiktokAudio?.tiktok_id ?? ''}
                    />
                </FormikProvider>
            </Modal.Body>
            <Modal.Actions>
                <Button
                    appearance="primary"
                    disabled={!formik.dirty || !formik.isValid || arePostsLoading}
                    isLoading={formik.isSubmitting || arePostsLoading}
                    onClick={formik.submitForm}
                >
                    Submit
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default UpdateTiktokCampaignModal;
